import { useMemo } from "react"
import {
  ButtonVariants,
  ButtonFilters,
  IconNames,
  SegmentedDatePicker,
  TextFilter,
  FilterType,
  Menu,
  MenuItem,
  ToggleSwitch,
} from '@triumph/design-system';
import { FileType } from '@/app/shared/constants/misc/FileType';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_DATE_FILTER,
  DEFAULT_EXCLUDED_STATUSES,
  FILTER_FIELDS,
  DATE_RANGE_OPTIONS,
  InvoicesDashboard,
  AvailableFilters,
} from '../dashboard.types';
import { ButtonFilterSelection } from '@/app/shared/utils/invoiceUtils/invoiceTypes';
import {
  FiltersContainer,
  FiltersRow,
  DatePickerWrapper,
  ButtonFiltersWrapper,
  SearchWrapper,
  SearchRow,
  TotalItemsRow,
  TotalItemsCount,
  DateRangeLabel,
  TotalItemsLeftSection,
  TotalItemsRightSection,
  ResetFiltersWrapper,
  FilterSeparator,
  ResetFiltersLink,
  NeedsAttentionWrapper,
} from './Invoices.StyledComponents';

interface InvoicesTableFiltersProps {
  rows: InvoicesDashboard[];
  totalInvoices: number;
  availableFilters: AvailableFilters;
  selectedDateRangeIndex: number;
  currentDateRange: { startDate: Date; endDate: Date } | null;
  handleDateRangeChange: (
    index: number,
    startDate: Date,
    endDate: Date,
  ) => void;
  needsAttention: boolean;
  handleNeedsAttentionChange: (needsAttention: boolean) => void;
  searchTerm: string;
  onSearchChange: (term: string) => void;
  buttonFilters: ButtonFilterSelection[];
  onButtonFilterChange: (column: string, value: string) => Promise<void>;
  onExportList: (fileType: FileType) => void;
  isExporting?: boolean;
  onResetFilters: () => void;
  hasActiveFilters: boolean;
  filterResetKey: number;
}

const defaultStatusOptions = DEFAULT_EXCLUDED_STATUSES.map((status) => ({
  value: status,
  originalValue: status,
}));

export const InvoicesTableFilters = ({
  rows,
  totalInvoices,
  availableFilters,
  selectedDateRangeIndex,
  currentDateRange,
  handleDateRangeChange,
  needsAttention,
  handleNeedsAttentionChange,
  searchTerm,
  onSearchChange,
  buttonFilters,
  onButtonFilterChange,
  onExportList,
  isExporting = false,
  onResetFilters,
  hasActiveFilters,
  filterResetKey,
}: InvoicesTableFiltersProps) => {
  const { t } = useTranslation('invoicesDashboard');

  const getDateRange = (months: number): { startDate: Date; endDate: Date } => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() - months);
    return { startDate, endDate };
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getDateRangeDisplay = (): string => {
    if (currentDateRange) {
      return `${formatDate(currentDateRange.startDate)} - ${formatDate(currentDateRange.endDate)}`;
    }

    const defaultRange = getDateRange(DEFAULT_DATE_FILTER.filterMonth);
    return `${formatDate(defaultRange.startDate)} - ${formatDate(defaultRange.endDate)}`;
  };

  const preSelectedFilters = useMemo<{ column: string; value: string }[]>(
    () => [],
    [],
  );

  const carrierOptions = useMemo(() => {
    const valuesFromData = rows
      .map((row) => row.clientName)
      .filter((val): val is string => Boolean(val));

    const availableCarriers =
      availableFilters.carrier?.map((car) => car.clientName) ?? [];

    const uniqueValues = [
      ...new Set([...valuesFromData, ...availableCarriers]),
    ];
    return uniqueValues.sort().map((value) => ({
      value: value,
      originalValue: value,
    }));
  }, [rows, availableFilters]);

  const statusOptions = useMemo(() => {
    const valuesFromData = rows
      .map((row) => row.status)
      .filter((val): val is string => Boolean(val));

    const allOptions = new Set([
      ...defaultStatusOptions.map((opt) => opt.value),
      ...valuesFromData,
      ...(availableFilters.statusDebtor ?? []),
    ]);
    return Array.from(allOptions)
      .sort()
      .map((value) => ({
        value: value,
        originalValue: value,
      }));
  }, [rows, availableFilters]);

  const ageOptions = useMemo(() => {
    const valuesFromData = rows
      .map((row) => row.age)
      .filter((val): val is string => val !== null && val !== undefined);

    const availableAges =
      availableFilters.age
        ?.filter((age): age is string => age != null)
        .map((age) => parseInt(age)) ?? [];

    const uniqueValues = [...new Set([...valuesFromData, ...availableAges])];
    return uniqueValues
      .sort((a, b) => Number(a) - Number(b))
      .map((value) => ({
        value: String(value),
        originalValue: String(value),
      }));
  }, [rows, availableFilters]);

  const getCarrierFilterData = async (searchTerm?: string | null) => {
    const options = searchTerm
      ? carrierOptions.filter((item) =>
          item.value.toLowerCase().includes(searchTerm.toLowerCase()),
        )
      : carrierOptions;
    return options.map((item) => ({ ...item }));
  };

  const getStatusFilterData = async (searchTerm?: string | null) => {
    const options = searchTerm
      ? statusOptions.filter((item) =>
          item.value.toLowerCase().includes(searchTerm.toLowerCase()),
        )
      : statusOptions;
    return options.map((item) => ({ ...item }));
  };

  const getAgeFilterData = async (searchTerm?: string | null) => {
    const options = searchTerm
      ? ageOptions.filter((item) =>
          item.value.toLowerCase().includes(searchTerm.toLowerCase()),
        )
      : ageOptions;
    return options.map((item) => ({ ...item }));
  };

  const datePickerOptions = DATE_RANGE_OPTIONS.map((option) => ({
    label: `${option.months} ${t('invoiceList.monthPluralLabel')}`,
    labelAbbreviation: `${option.months} mo`,
    onClick: () => {
      const { startDate, endDate } = getDateRange(option.months);
      handleDateRangeChange(option.index, startDate, endDate);
    },
  }));

  return (
    <FiltersContainer>
      <FiltersRow>
        <DatePickerWrapper>
          <SegmentedDatePicker
            key={`date-picker-${filterResetKey}`}
            onDateSelected={(startDate, endDate) => {
              if (startDate && endDate) {
                handleDateRangeChange(
                  selectedDateRangeIndex,
                  startDate,
                  endDate,
                );
              }
            }}
            offsetX={0}
            offsetY={12}
            startDate={
              currentDateRange?.startDate ??
              getDateRange(DEFAULT_DATE_FILTER.filterMonth).startDate
            }
            endDate={currentDateRange?.endDate ?? new Date()}
            defaultSelectedIndex={selectedDateRangeIndex}
            options={datePickerOptions}
          />
        </DatePickerWrapper>
        <ButtonFiltersWrapper>
          <ButtonFilters
            key={`button-filters-${filterResetKey}`}
            id="invoiceFilters"
            filters={[
              {
                id: 'carrierFilter',
                column: FILTER_FIELDS.clientName,
                filterType: FilterType.Button,
                label: 'Carrier',
                data: [],
                getData: getCarrierFilterData,
                disableSearch: false,
                title: 'Carrier',
                header: 'Carrier',
              },
              {
                id: 'statusFilter',
                column: FILTER_FIELDS.status,
                filterType: FilterType.Button,
                label: 'Status',
                data: [],
                getData: getStatusFilterData,
                disableSearch: false,
                title: 'Status',
                header: 'Status',
              },
              {
                id: 'ageFilter',
                column: FILTER_FIELDS.age,
                filterType: FilterType.Button,
                label: 'Age',
                data: [],
                getData: getAgeFilterData,
                disableSearch: true,
                title: 'Age',
                header: 'Age',
              },
            ]}
            onButtonFilterSearch={onButtonFilterChange}
            hiddenColumns={[]}
            hideResetFilter={true}
            preSelectedFilters={[]}
          />
          <FilterSeparator />
          <NeedsAttentionWrapper>
            {t('invoiceList.needsAttention', 'Needs Attention')}
            <ToggleSwitch
              checked={needsAttention}
              onChange={() => handleNeedsAttentionChange(!needsAttention)}
            ></ToggleSwitch>
          </NeedsAttentionWrapper>
        </ButtonFiltersWrapper>

        {hasActiveFilters && (
          <ResetFiltersWrapper>
            <FilterSeparator />
            <ResetFiltersLink onClick={onResetFilters}>
              {t('invoiceList.resetFilters', 'Reset Filters')}
            </ResetFiltersLink>
          </ResetFiltersWrapper>
        )}
      </FiltersRow>
      <SearchRow>
        <SearchWrapper>
          <TextFilter
            key={`search-filter-${filterResetKey}`}
            displayShowHideButtonFilters={false}
            onSearchTermChange={onSearchChange}
            searchPlaceholderText={
              t('invoiceList.searchPlaceholderText') || 'Search invoices...'
            }
            searchDefaultValue={searchTerm}
          />
        </SearchWrapper>
      </SearchRow>
      <TotalItemsRow>
        <TotalItemsLeftSection>
          <TotalItemsCount>
            {totalInvoices.toLocaleString()} {t('invoiceList.items')}
          </TotalItemsCount>
          <DateRangeLabel>{getDateRangeDisplay()}</DateRangeLabel>
        </TotalItemsLeftSection>
        <TotalItemsRightSection>
          <Menu
            variant={ButtonVariants.tertiary}
            icon={IconNames.Interface_Download}
            iconLeft={true}
            label={isExporting ? 'Exporting...' : 'Export List'}
            menuTestId="export-list-menu"
            bold={true}
          >
            <MenuItem
              label="Excel Report"
              icon={IconNames.Filetype_Excel}
              iconLeft={true}
              onClick={() => onExportList(FileType.Xlsx)}
              menuItemTestId="export-xlsx-button"
            />
            <MenuItem
              label="PDF Report"
              icon={IconNames.Filetype_PDF}
              iconLeft={true}
              onClick={() => onExportList(FileType.Pdf)}
              menuItemTestId="export-pdf-button"
            />
          </Menu>
        </TotalItemsRightSection>
      </TotalItemsRow>
    </FiltersContainer>
  );
};
